import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { BoardMember } from '../api/client'
import { Avatar } from './Avatar'

/*
  Choosing who answers.

  The default is the chair, and the chair pulls in whoever owns the ground when the
  question sits squarely on one discipline. That is right nearly always, which is why this
  is a quiet link under the ask box rather than a control the owner has to operate before
  he can type. He should be able to ignore it for months.

  When he does open it, every seat leads with its discipline. "Priya Raghavan, CFO" says
  nothing an owner can pick on; "Finance" is the thing he is actually choosing between,
  and it is read off the seat's own attribute vector rather than typed in somewhere, so a
  seat retuned on the Board screen introduces itself differently the next day.
*/

export function SeatPicker({
  board, chosen, onChoose, onClose,
}: {
  board: BoardMember[]
  chosen: BoardMember | null
  onChoose: (member: BoardMember | null) => void
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // The dissenter is a meeting device (§4.1). It has a standing mandate to argue against
  // whatever the room is heading towards, which is exactly what you do not want from the
  // only voice answering a question on a Tuesday.
  const askable = board.filter(m => m.seatRole !== 'dissenter')
  const dissenters = board.filter(m => m.seatRole === 'dissenter')

  /*
    Portalled to the body rather than rendered in place. The Home ask box opens this from
    inside a widget card, and a transformed ancestor makes position:fixed resolve against
    that card instead of the viewport: the backdrop covered one column and the sheet
    opened half off the top of the screen.
  */
  return createPortal(
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet sheet--picker" onClick={e => e.stopPropagation()} role="dialog" aria-label="Who answers">
        <div className="picker__title">Who should answer?</div>

        <button
          className={`seat-opt${chosen === null ? ' is-chosen' : ''}`}
          onClick={() => { onChoose(null); onClose() }}
        >
          <span className="seat-opt__face seat-opt__face--all" aria-hidden>
            <IconGavel />
          </span>
          <span className="seat-opt__body">
            <span className="seat-opt__name">The chair takes it</span>
            <span className="seat-opt__disc">Default</span>
            <span className="seat-opt__note">
              And brings in whoever owns the ground when the question sits on one.
            </span>
          </span>
        </button>

        <div className="picker__rule" role="separator" />

        {askable.map(m => (
          <button
            key={m.id}
            className={`seat-opt${chosen?.id === m.id ? ' is-chosen' : ''}`}
            onClick={() => { onChoose(m); onClose() }}
          >
            <span className="seat-opt__face">
              <Avatar member={m} size="sm" />
            </span>
            <span className="seat-opt__body">
              <span className="seat-opt__name">
                {m.displayName}
                {m.seatRole === 'chair' && <span className="seat-opt__role">Chair</span>}
              </span>
              <span className="seat-opt__disc">{m.discipline}</span>
              {m.backstory && <span className="seat-opt__note">{firstLine(m.backstory)}</span>}
            </span>
          </button>
        ))}

        {dissenters.length > 0 && (
          <p className="picker__foot">
            {dissenters.length === 1 ? dissenters[0]!.displayName.split(' ')[0] : 'The dissenting seat'} is
            not on this list. Arguing the other side is a meeting job, and it is the one
            thing you do not want from the only person answering.
          </p>
        )}
      </div>
    </div>,
    document.body,
  )
}

/* One sentence of the backstory. The full thing is three paragraphs and belongs on Board. */
function firstLine(backstory: string): string {
  const first = backstory.trim().split(/(?<=[.!?])\s+/)[0] ?? ''
  return first.length > 120 ? `${first.slice(0, 119).trimEnd()}…` : first
}

function IconGavel() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
         strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 20h9M6.5 13.5l5-5M9 16l7.5-7.5M13.5 4 20 10.5M15.5 2.5 21.5 8.5" />
    </svg>
  )
}
